/**
 * FERP Portal - Interactive Stat Card (DRY)
 *
 * Reusable stat card with total + "new" split counts.
 * Used by TabulatorPrimitiveManager (all 25 primitives), DocumentManager,
 * and any page that needs count summaries with clickable filtering.
 *
 * Features:
 * - Left side: total count (clickable, shows "all" label)
 * - Right side: new/unviewed count (clickable, shows "new" label)
 * - Active state highlight when filter is engaged
 *
 * Usage:
 *   <InteractiveStatCard
 *     label="Total"
 *     total={data.length}
 *     newCount={data.filter(d => !tracker.isViewed(d.id)).length}
 *     color="cyan"
 *     isActive={activeFilter === 'total'}
 *     activeScope={activeFilter === 'total' ? currentScope : null}
 *     onClickAll={() => setFilter('total', 'all')}
 *     onClickNew={() => setFilter('total', 'new')}
 *   />
 */

import { Text, Group, Paper } from '@mantine/core';

export type StatFilterScope = 'all' | 'new';

interface InteractiveStatCardProps {
  label: string;
  total: number;
  newCount: number;
  color: string;
  icon?: React.ReactNode;
  isActive: boolean;
  activeScope: StatFilterScope | null;
  onClickAll: () => void;
  onClickNew: () => void;
}

export function InteractiveStatCard({
  label,
  total,
  newCount,
  color,
  icon,
  isActive,
  activeScope,
  onClickAll,
  onClickNew,
}: InteractiveStatCardProps) {
  return (
    <Paper withBorder p="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" mb="xs">{label}</Text>
      <Group gap={0} grow>
        {/* Left: total count — clickable */}
        <div
          onClick={onClickAll}
          style={{
            cursor: 'pointer',
            paddingRight: 8,
            borderRadius: '4px 0 0 4px',
            background: isActive && activeScope === 'all' ? `var(--mantine-color-${color}-0)` : undefined,
            transition: 'background 0.15s ease',
          }}
        >
          <Group gap="xs">
            <Text size="xl" fw={700} c={isActive && activeScope === 'all' ? color : (total > 0 ? color : 'dimmed')}>
              {total}
            </Text>
            {icon}
          </Group>
          <Text size="xs" c="dimmed">all</Text>
        </div>
        {/* Right: new count — clickable */}
        <div
          onClick={onClickNew}
          style={{
            cursor: 'pointer',
            paddingLeft: 8,
            borderLeft: '1px solid var(--mantine-color-gray-3)',
            borderRadius: '0 4px 4px 0',
            background: isActive && activeScope === 'new' ? 'var(--mantine-color-orange-0)' : undefined,
            transition: 'background 0.15s ease',
          }}
        >
          <Group gap={2}>
            <Text size="lg" fw={600} c={newCount > 0 ? 'orange' : 'dimmed'}>{newCount}</Text>
            <Text size="xs" c="dimmed">new</Text>
          </Group>
        </div>
      </Group>
    </Paper>
  );
}
